import { Component, computed, inject, input, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { MyBidsPageComponent } from './my-bids-page/my-bids-page.component';
import { BidderCommunityMenuService } from '../../../services/bidder-community/bidder-community-menu/bidder-community-menu.service';
import { SelectModule } from 'primeng/select';
import { isPlatformBrowser, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PaginatorModule } from 'primeng/paginator';
import { MyBidsService } from '../../../services/bidder-community/my-bids/my-bids.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AbsoluteMediaUriService } from '../../../services/absolute-media-uri.service';
import { environment } from '../../../../environments/environment';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { AuthService } from '../../../services/authentication/auth.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RouterModule } from '@angular/router';


@Component({
  selector: 'app-my-bids',
  imports: [MyBidsPageComponent,
    SelectModule,
    PaginatorModule,
    FormsModule,
    ProgressSpinnerModule,
    NgIf,
    RouterModule,
  ],
  templateUrl: './my-bids.component.html',
  styleUrl: './my-bids.component.scss'
})
export class MyBidsComponent implements OnInit {

  public id = input(0);

  private readonly bidderCommunityMenuService = inject(BidderCommunityMenuService);
  protected readonly absoluteMediaUriService = inject(AbsoluteMediaUriService);
  private readonly myBidsService = inject(MyBidsService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly authService = inject(AuthService);


  authSubscription!: Subscription;
  isAuthenticated = this.oidcSecurityService.authenticated;

  isLoading = this.myBidsService.isLoading;

  // paginator
  page: number = 0;
  rowsPerPageOptions = [1, 2, 6, 10];
  first = signal(0);
  rows = signal(6);

  selectedSortSignal = signal('TIMELEFT_ASC');

  paginatedItems = computed(() =>
    this.sortedItems().slice(this.first(), this.first() + this.rows())
  );

  myBids = this.myBidsService.myBidsSignal;

  sortFilters = [
    { label: 'Item Name - Ascending', value: 'NAME_ASC' },
    { label: 'Item Name - Descending', value: 'NAME_DESC' },
    { label: 'Org Name - Ascending', value: 'ORG_ASC' },
    { label: 'Org Name - Descending', value: 'ORG_DESC' },
    { label: 'Max Bid - Ascending', value: 'MAXBID_ASC' },
    { label: 'Max Bid - Descending', value: 'MAXBID_DESC' },
    { label: 'Leading Bid - Ascending', value: 'HIGHBID_ASC' },
    { label: 'Leading Bid - Descending', value: 'HIGHBID_DESC' },
    { label: 'Time Left - Ascending', value: 'TIMELEFT_ASC' },
    { label: 'Time Left - Descending', value: 'TIMELEFT_DESC' },
  ];

  sortedItems = computed(() => {
    const sortValue = this.selectedSortSignal();
    return [...this.myBids()].sort((a, b) => {

      switch (sortValue) {
        case 'NAME_ASC':
          return a.itemName.localeCompare(b.itemName);
        case 'NAME_DESC':
          return b.itemName.localeCompare(a.itemName);

        case 'ORG_ASC':
          return a.organizationName.localeCompare(b.organizationName);
        case 'ORG_DESC':
          return b.organizationName.localeCompare(a.organizationName);

        case 'MAXBID_ASC': {
          const aMax = parseFloat((a.myMaxBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          const bMax = parseFloat((b.myMaxBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          return aMax - bMax;
        }

        case 'MAXBID_DESC': {
          const aMax = parseFloat((a.myMaxBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          const bMax = parseFloat((b.myMaxBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          return bMax - aMax;
        }
        case 'TIMELEFT_DESC':
          {
            const now = new Date();
            const aClosed = new Date(a.itemClosingDate) < now;
            const bClosed = new Date(b.itemClosingDate) < now;

            if (aClosed !== bClosed) {
              return aClosed ? -1 : 1;
            }

            const diff = new Date(b.itemClosingDate).getTime() - new Date(a.itemClosingDate).getTime();
            if (diff !== 0) return diff;

            return b.itemName.localeCompare(a.itemName);
          }
        case 'TIMELEFT_ASC':
        default: {
          const now = new Date();
          const aClosed = new Date(a.itemClosingDate) < now;
          const bClosed = new Date(b.itemClosingDate) < now;

          if (aClosed !== bClosed) {
            return aClosed ? 1 : -1;
          }

          const diff = new Date(a.itemClosingDate).getTime() - new Date(b.itemClosingDate).getTime();
          if (diff !== 0) return diff;

          return a.itemName.localeCompare(b.itemName);
        }
      }
    });
  });

  ngOnInit(): void {
    setTimeout(() => {
      this.bidderCommunityMenuService.setPageData(
        {
          id: this.id(),
          pageName: "My Bids",
          pageRoute: "my-bids"
        }
      );

      if (typeof window !== 'undefined') {
        window.onpageshow = function (event) {
          if (event.persisted) {
            window.location.reload();
          }
        };
      }

      if (this.isAuthenticated().isAuthenticated) {
        this.getMyBids();
      } else {
        if (isPlatformBrowser(this.platformId)) {
          if (typeof window !== 'undefined') {
            window.open(environment.mediaUrl, '_self');
          }
          return;
        }
      }

      this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {
        if (isAuth) {
          this.getMyBids();
        } else {
          if (isPlatformBrowser(this.platformId)) {
            if (typeof window !== 'undefined') {
              window.open(environment.mediaUrl, '_self');
            }
            return;
          }
        }
      });
    },100);
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
  }


  onPageChange(event: any) {
    this.first.set(event.first);
    this.rows.set(event.rows);
  }

  getMyBids() {
    this.myBidsService.getMyBids().subscribe({
      next: (response) => {
      },
      error: (err) => {
        console.error('Error detectado:', err.message);
      }
    });

  }


}
